"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { PinContainer } from "@/components/ui/3d-pin";
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Layout,
  Plus,
  Sparkles,
  CheckCircle,
  Award,
  Clock,
  Shield,
  Headphones,
  Search,
  TrendingUp,
  Users,
  Star,
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchServices, fetchProjects } from "@/store/slices/landingSlice";
import { imageUrl } from "@/services/BaseUrl";
import { slugify } from "@/utils/slugify";
import type { Project } from "@/types/landing";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const getProjectImageUrl = (img?: string): string | null => {
  if (!img || typeof img !== "string") return null;
  const trimmed = img.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }
  const cleanPath = trimmed.replace(/\\/g, "/").replace(/^\/+/, "");
  return `${imageUrl}/${cleanPath}`;
};

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

// ─── Types ────────────────────────────────────────────────────────────────────

type FeatureItem = { icon: string; title: string; description: string };
type ProcessStep = {
  step: number;
  icon: string;
  title: string;
  description: string;
  color: string;
};
type StatItem = { value: string; label: string };
type FAQItem = { question: string; answer: string };

type ServiceContent = {
  tagline: string;
  processBadge?: string;
  processTitle?: string;
  processDescription?: string;
  features: FeatureItem[];
  process: ProcessStep[];
  stats: StatItem[];
  techStack: string[];
  faq: FAQItem[];
};

// ─── Service Content Map ──────────────────────────────────────────────────────

const SERVICE_CONTENT: Record<string, ServiceContent> = {
  "__disabled_web-development": {
    tagline: "Building Fast, Beautiful & Scalable Web Experiences",
    features: [
      {
        icon: "Paintbrush",
        title: "Custom Web Design",
        description:
          "Pixel-perfect designs tailored to your brand identity and business goals.",
      },
      {
        icon: "Smartphone",
        title: "Responsive Layouts",
        description:
          "Flawless experience across desktop, tablet, and mobile — every screen, every time.",
      },
      {
        icon: "Zap",
        title: "Performance Optimization",
        description:
          "Lightning-fast load times with Core Web Vitals and Lighthouse scores above 95.",
      },
      {
        icon: "Search",
        title: "SEO Architecture",
        description:
          "Built-in technical SEO from semantic markup to structured data schemas.",
      },
      {
        icon: "Database",
        title: "CMS Integration",
        description:
          "Flexible content management with WordPress, Contentful, or any headless CMS.",
      },
      {
        icon: "ShoppingCart",
        title: "E-commerce Solutions",
        description:
          "Full shopping carts, secure payment gateways, and inventory management systems.",
      },
    ],
    process: [
      {
        step: 1,
        icon: "Search",
        title: "Discovery & Planning",
        description:
          "We analyze your goals, audience, competitors, and technical requirements to build a clear project roadmap.",
        color: "#8B31FF",
      },
      {
        step: 2,
        icon: "Layout",
        title: "Wireframing & Design",
        description:
          "Creating detailed wireframes and high-fidelity Figma mockups approved before a single line of code is written.",
        color: "#8B31FF",
      },
      {
        step: 3,
        icon: "Code2",
        title: "Development",
        description:
          "Writing clean, scalable, and well-tested code using modern frameworks and industry best practices.",
        color: "#6D28D9",
      },
      {
        step: 4,
        icon: "TestTube2",
        title: "QA & Testing",
        description:
          "Rigorous cross-browser, device, performance, and accessibility testing before launch.",
        color: "#101bb9",
      },
      {
        step: 5,
        icon: "Rocket",
        title: "Launch & Support",
        description:
          "Smooth deployment, post-launch monitoring, and ongoing maintenance to keep you growing.",
        color: "#2e29b6",
      },
    ],
    stats: [
      { value: "50+", label: "Websites Delivered" },
      { value: "97%", label: "Client Satisfaction" },
      { value: "4.0/5", label: "Average Rating" },
      { value: "5+", label: "Years Experience" },
    ],
    techStack: [
      "React",
      "Next.js",
      "Vue.js",
      "Angular",
      "TypeScript",
      "Node.js",
      "Python",
      "Django",
      ".NET",
      "PostgreSQL",
      "Tailwind CSS",
      "Vercel",
      "Hostinger",
      "IONOS",
      "aaPanel",
    ],
    faq: [
      {
        question: "How long does it take to build a website?",
        answer:
          "Most websites are delivered in 4–8 weeks. Simple informational sites can launch in 2 weeks; complex e-commerce platforms may take 12+ weeks. We provide a precise timeline after your discovery call.",
      },
      {
        question: "What technology stack do you use?",
        answer:
          "We primarily build with React and Next.js for the frontend, Node.js or Python for the backend, and PostgreSQL for databases. We always choose the stack best suited for your specific requirements and scale.",
      },
      {
        question: "Will my website rank well on Google?",
        answer:
          "SEO is built into every website we deliver — semantic HTML, Core Web Vitals optimization, structured data markup, clean URL structures, and fast page speeds. We also offer ongoing SEO services for continued growth.",
      },
      {
        question: "Can you redesign my existing website?",
        answer:
          "Yes. We audit your current site, preserve your existing SEO equity, improve UX and performance, and rebuild it on a modern stack — all without losing your hard-earned search rankings.",
      },
      {
        question: "Do you provide hosting and maintenance?",
        answer:
          "We help set up hosting on Vercel, AWS, or your preferred provider, and configure CDN and caching. We also offer monthly maintenance plans that include updates, backups, uptime monitoring, and priority bug fixes.",
      },
      {
        question: "What is included in the final delivery?",
        answer:
          "You receive the complete source code, deployment to your server/hosting, a short handover session, documentation, and 30 days of free post-launch support. Everything is fully yours — no vendor lock-in.",
      },
    ],
  },
  "app-development": {
    tagline: "Powerful Mobile Apps That Drive Real User Engagement",
    features: [
      {
        icon: "Smartphone",
        title: "iOS & Android",
        description:
          "Native and cross-platform apps delivering the best experience on both platforms.",
      },
      {
        icon: "Heart",
        title: "Intuitive UI/UX",
        description:
          "User-centered design that maximizes engagement, retention, and in-app conversions.",
      },
      {
        icon: "Cloud",
        title: "Backend Integration",
        description:
          "Robust REST/GraphQL APIs, scalable databases, and cloud infrastructure.",
      },
      {
        icon: "Bell",
        title: "Push Notifications",
        description:
          "Real-time personalized alerts that keep users engaged and coming back.",
      },
      {
        icon: "WifiOff",
        title: "Offline Capability",
        description:
          "Seamless functionality without internet using intelligent local caching strategies.",
      },
      {
        icon: "BarChart2",
        title: "Analytics & Insights",
        description:
          "Deep behavioral analytics and performance dashboards for continuous improvement.",
      },
    ],
    process: [
      {
        step: 1,
        icon: "Lightbulb",
        title: "Ideation & Strategy",
        description:
          "Validating your app concept, defining MVP features, and planning the full product roadmap.",
        color: "#8B31FF",
      },
      {
        step: 2,
        icon: "PenTool",
        title: "UI/UX Design",
        description:
          "Creating intuitive wireframes, interactive prototypes, and high-fidelity visual designs.",
        color: "#8B31FF",
      },
      {
        step: 3,
        icon: "Code2",
        title: "Development",
        description:
          "Building with React Native, Flutter, or native Swift/Kotlin for optimal performance.",
        color: "#6D28D9",
      },
      {
        step: 4,
        icon: "Shield",
        title: "QA Testing",
        description:
          "Device testing matrix, OS version coverage, and real-world usage scenario validation.",
        color: "#8B31FF",
      },
      {
        step: 5,
        icon: "Store",
        title: "App Store Launch",
        description:
          "Publishing, App Store Optimization (ASO), and full launch strategy execution.",
        color: "#8B31FF",
      },
    ],
    stats: [
      { value: "150+", label: "Apps Launched" },
      { value: "420K+", label: "Total Downloads" },
      { value: "4.8★", label: "App Store Rating" },
      { value: "40+", label: "Enterprise Clients" },
    ],
    techStack: [
      "React Native",
      "Flutter",
      "Swift",
      "Kotlin",
      "Firebase",
      "Node.js",
      "AWS",
      "Stripe",
    ],
    faq: [
      {
        question: "How long does it take to build a mobile app?",
        answer:
          "A simple MVP takes 8–12 weeks. A full-featured app typically takes 16–24 weeks, depending on complexity, integrations required, and design scope. We'll give you a precise estimate after reviewing your requirements.",
      },
      {
        question: "Do you build for both iOS and Android?",
        answer:
          "Yes. We build cross-platform apps using React Native or Flutter, giving you a single codebase that runs natively on both platforms with excellent performance and consistent UI.",
      },
      {
        question: "How much does mobile app development cost?",
        answer:
          "Simple MVPs start from approximately $15,000. Full-featured apps typically range from $30,000 to $100,000+ depending on complexity, number of screens, and custom backend requirements.",
      },
      {
        question: "Will you submit my app to the App Store and Google Play?",
        answer:
          "Absolutely. We handle the complete App Store Connect and Google Play Console submission, including all required assets, descriptions, and App Store Optimization (ASO) to maximize discoverability.",
      },
      {
        question: "Can you integrate payments and third-party APIs?",
        answer:
          "Yes. We integrate Stripe, PayPal, Apple Pay, Google Pay, social logins, push notifications, maps, analytics, and virtually any third-party API or service your app requires.",
      },
      {
        question: "Do you provide post-launch support?",
        answer:
          "Yes. We offer maintenance plans covering bug fixes, OS compatibility updates, new feature development, and real-time crash/performance monitoring after launch.",
      },
    ],
  },
  "wordpress-development": {
    tagline: "Custom WordPress Sites Built for Speed, Growth & Performance",
    features: [
      {
        icon: "Palette",
        title: "Custom Theme Development",
        description:
          "Unique WordPress themes coded from scratch — no page builders, pure maintainable code.",
      },
      {
        icon: "Puzzle",
        title: "Plugin Development",
        description:
          "Custom WordPress plugins built precisely for your specific business requirements.",
      },
      {
        icon: "ShoppingBag",
        title: "WooCommerce Stores",
        description:
          "Full-featured online stores with advanced shipping, payments, and product management.",
      },
      {
        icon: "Gauge",
        title: "Speed Optimization",
        description:
          "Consistently achieving 90+ PageSpeed scores through caching, CDN, and code optimization.",
      },
      {
        icon: "Lock",
        title: "Security Hardening",
        description:
          "Enterprise-grade security with firewalls, malware scanning, and regular security audits.",
      },
      {
        icon: "RefreshCw",
        title: "Ongoing Maintenance",
        description:
          "Regular updates, automated backups, 24/7 uptime monitoring, and technical support.",
      },
    ],
    process: [
      {
        step: 1,
        icon: "ClipboardList",
        title: "Requirements Gathering",
        description:
          "Detailed specification of features, plugins, hosting requirements, and project timeline.",
        color: "#8B31FF",
      },
      {
        step: 2,
        icon: "PenTool",
        title: "Design Phase",
        description:
          "Custom Figma designs for every page template, fully approved before development begins.",
        color: "#8B31FF",
      },
      {
        step: 3,
        icon: "Code2",
        title: "Theme Development",
        description:
          "Building pixel-perfect custom themes with clean, maintainable PHP and JavaScript.",
        color: "#6D28D9",
      },
      {
        step: 4,
        icon: "Plug",
        title: "Plugin & Integrations",
        description:
          "Adding required plugins and connecting third-party services and payment gateways.",
        color: "#8B31FF",
      },
      {
        step: 5,
        icon: "Rocket",
        title: "Testing & Launch",
        description:
          "Cross-browser QA, performance benchmarking, and smooth production deployment.",
        color: "#8B31FF",
      },
    ],
    stats: [
      { value: "180+", label: "WP Sites Built" },
      { value: "99.8%", label: "Uptime SLA" },
      { value: "38%", label: "Avg. Speed Boost" },
      { value: "6+", label: "Years WP Expertise" },
    ],
    techStack: [
      "WordPress",
      "PHP",
      "WooCommerce",
      "MySQL",
      "Redis",
      "Nginx",
      "Elementor",
      "ACF",
    ],
    faq: [
      {
        question: "Do you build custom WordPress themes or use templates?",
        answer:
          "We build fully custom themes from scratch using PHP, HTML, CSS, and JavaScript. No Divi, no Elementor bloat — just clean, fast, maintainable code.",
      },
      {
        question: "Can you build a WooCommerce store?",
        answer:
          "Absolutely. We build complete WooCommerce stores with custom product pages, advanced filtering, multiple payment gateways, shipping integrations, and inventory management.",
      },
      {
        question: "My WordPress site is slow — can you fix it?",
        answer:
          "Yes. We perform full performance audits and implement server-side caching, CDN, image optimization, database cleanup, and code minification — typically improving scores by 40%+.",
      },
      {
        question: "Can you migrate my existing site to WordPress?",
        answer:
          "Yes. We migrate from any platform (Wix, Squarespace, Shopify, custom) to WordPress while preserving all content, SEO metadata, redirects, and URL structures.",
      },
      {
        question: "How do you secure a WordPress site?",
        answer:
          "We implement a full security stack: login protection, two-factor authentication, WAF (Web Application Firewall), malware scanning, SSL, and regular automated backups with offsite storage.",
      },
      {
        question: "Will I be able to manage the site myself?",
        answer:
          "Yes. WordPress is designed for non-technical users. We configure an intuitive admin dashboard, add custom page builder blocks if needed, and provide a handover training session.",
      },
    ],
  },
  "software-development": {
    tagline: "Enterprise Software Solutions Built for Scale & Reliability",
    features: [
      {
        icon: "Building2",
        title: "Custom Enterprise Software",
        description:
          "Tailored solutions that automate and optimize your unique business processes end-to-end.",
      },
      {
        icon: "TrendingUp",
        title: "Scalable Architecture",
        description:
          "Systems engineered to handle 10x growth without any performance degradation.",
      },
      {
        icon: "Cloud",
        title: "Cloud-Native Solutions",
        description:
          "AWS, Azure, and GCP native applications with intelligent auto-scaling capabilities.",
      },
      {
        icon: "Share2",
        title: "API Development",
        description:
          "RESTful and GraphQL APIs with comprehensive docs, versioning, and developer SDKs.",
      },
      {
        icon: "Database",
        title: "Database Design",
        description:
          "Optimized schemas, advanced query tuning, and zero-downtime data migrations.",
      },
      {
        icon: "GitBranch",
        title: "DevOps & CI/CD",
        description:
          "Automated deployment pipelines for faster, safer, and more reliable software releases.",
      },
    ],
    process: [
      {
        step: 1,
        icon: "Search",
        title: "Requirements Analysis",
        description:
          "Deep discovery of business processes, technical constraints, and success criteria.",
        color: "#8B31FF",
      },
      {
        step: 2,
        icon: "Network",
        title: "System Architecture",
        description:
          "Designing scalable, maintainable architecture with the optimal technology stack.",
        color: "#8B31FF",
      },
      {
        step: 3,
        icon: "Code2",
        title: "Agile Development",
        description:
          "Sprint-based delivery with weekly demos and continuous client feedback loops.",
        color: "#6D28D9",
      },
      {
        step: 4,
        icon: "TestTube2",
        title: "Quality Assurance",
        description:
          "Automated unit, integration, and end-to-end tests ensuring rock-solid reliability.",
        color: "#8B31FF",
      },
      {
        step: 5,
        icon: "Monitor",
        title: "Deployment & Monitoring",
        description:
          "Infrastructure-as-code, CI/CD pipelines, and real-time production monitoring.",
        color: "#8B31FF",
      },
    ],
    stats: [
      { value: "120+", label: "Systems Built" },
      { value: "99.9%", label: "Uptime SLA" },
      { value: "35%", label: "Faster Delivery" },
      { value: "80+", label: "Integrations Built" },
    ],
    techStack: [
      "Node.js",
      "Python",
      "Go",
      "PostgreSQL",
      "Redis",
      "Docker",
      "Kubernetes",
      "AWS",
    ],
    faq: [
      {
        question: "What types of software do you build?",
        answer:
          "We build SaaS platforms, internal business tools, ERPs, CRMs, marketplaces, data pipelines, automation systems, and any custom enterprise software requiring complex business logic.",
      },
      {
        question: "How do you handle project requirements?",
        answer:
          "We run structured discovery workshops to map your business processes, define system boundaries, create technical specs, and build a detailed project plan before writing any code.",
      },
      {
        question: "Do you follow agile development?",
        answer:
          "Yes. We use 2-week sprints with planning sessions, daily standups (async or live), sprint reviews with working demos, and retrospectives to continuously improve delivery quality.",
      },
      {
        question: "Can you integrate with our existing systems?",
        answer:
          "Absolutely. We've integrated with hundreds of enterprise systems including Salesforce, SAP, HubSpot, payment processors, ERP platforms, legacy databases, and proprietary internal APIs.",
      },
      {
        question: "How do you ensure software quality?",
        answer:
          "Every project includes unit tests, integration tests, and end-to-end tests with target coverage above 80%. We also run static analysis, security scanning, and load testing before any production release.",
      },
      {
        question: "Who owns the code and IP?",
        answer:
          "You own 100% of the code, intellectual property, and all associated assets from day one. We sign IP assignment agreements at the start of every engagement.",
      },
    ],
  },
  "shopify-development": {
    tagline: "Shopify Stores That Convert Visitors into Loyal Customers",
    features: [
      {
        icon: "Palette",
        title: "Custom Shopify Themes",
        description:
          "Unique Liquid theme development that reflects your brand and maximizes conversions.",
      },
      {
        icon: "Puzzle",
        title: "App Development",
        description:
          "Custom Shopify apps extending your store well beyond what the App Store offers.",
      },
      {
        icon: "CreditCard",
        title: "Payment Integration",
        description:
          "Multiple gateways: Stripe, PayPal, Klarna, and Buy Now Pay Later solutions.",
      },
      {
        icon: "Package",
        title: "Inventory Management",
        description:
          "Advanced tracking, multi-location support, and automated low-stock alerts.",
      },
      {
        icon: "Search",
        title: "SEO & Performance",
        description:
          "Schema markup, Core Web Vitals optimization, and collection page SEO architecture.",
      },
      {
        icon: "BarChart2",
        title: "Analytics & Reporting",
        description:
          "Custom dashboards tracking revenue, AOV, LTV, and multi-touch attribution.",
      },
    ],
    process: [
      {
        step: 1,
        icon: "Target",
        title: "Store Strategy",
        description:
          "Defining brand positioning, product catalog architecture, and customer journey mapping.",
        color: "#8B31FF",
      },
      {
        step: 2,
        icon: "PenTool",
        title: "Design & UX",
        description:
          "Conversion-optimized store designs with A/B testing strategy built in from day one.",
        color: "#8B31FF",
      },
      {
        step: 3,
        icon: "Code2",
        title: "Theme Development",
        description:
          "Building robust Liquid themes with Shopify 2.0 sections and full customizability.",
        color: "#6D28D9",
      },
      {
        step: 4,
        icon: "Plug",
        title: "App Integration",
        description:
          "Connecting email, reviews, subscriptions, loyalty programs, and custom functionality.",
        color: "#8B31FF",
      },
      {
        step: 5,
        icon: "TrendingUp",
        title: "Launch & Growth",
        description:
          "Comprehensive UAT, live launch, CRO sprints, and ongoing revenue optimization.",
        color: "#8B31FF",
      },
    ],
    stats: [
      { value: "180+", label: "Shopify Stores" },
      { value: "$2.4M+", label: "Client Revenue" },
      { value: "18%", label: "Avg. Conv. Lift" },
      { value: "24/7", label: "Support Available" },
    ],
    techStack: [
      "Shopify",
      "Liquid",
      "JavaScript",
      "Shopify Plus",
      "Klaviyo",
      "ReCharge",
      "Stripe",
      "GraphQL",
    ],
    faq: [
      {
        question: "Do you build custom Shopify themes or use templates?",
        answer:
          "We build fully custom Liquid themes tailored to your brand — no paid templates. Custom themes load faster, convert better, and give you a unique look that templates can't match.",
      },
      {
        question:
          "Can you migrate my store from WooCommerce or another platform?",
        answer:
          "Yes. We migrate product catalogs, customer data, order history, and blog content from any platform to Shopify with zero data loss and properly redirected URLs to protect your SEO.",
      },
      {
        question: "Do you work with Shopify Plus?",
        answer:
          "Absolutely. We build enterprise-grade Shopify Plus stores with custom checkout extensions, B2B pricing rules, multi-currency/multi-language setups, and advanced integrations.",
      },
      {
        question: "What apps do you typically integrate?",
        answer:
          "Common integrations include Klaviyo (email), Yotpo/Judge.me (reviews), ReCharge (subscriptions), Gorgias (support), Loox (photo reviews), Meta/Google Shopping, and custom ERP/WMS systems.",
      },
      {
        question: "How do you improve my store's conversion rate?",
        answer:
          "We audit your existing funnel, optimize product pages, checkout flow, mobile experience, page speed, and trust signals. Clients typically see a 25–40% conversion rate improvement.",
      },
      {
        question: "Can you build a custom Shopify app?",
        answer:
          "Yes. We build custom public and private Shopify apps using the Shopify REST and GraphQL Admin APIs, covering any functionality your store needs beyond standard apps.",
      },
    ],
  },
  "wix-development": {
    tagline: "Professional Wix Websites That Stand Apart from the Crowd",
    features: [
      {
        icon: "Paintbrush",
        title: "Custom Wix Design",
        description:
          "Stunning, bespoke designs that go far beyond any standard Wix template.",
      },
      {
        icon: "Code2",
        title: "Velo Development",
        description:
          "Advanced JavaScript functionality using Wix Velo for complex, dynamic features.",
      },
      {
        icon: "Search",
        title: "SEO Configuration",
        description:
          "Full technical, on-page, and structured data SEO configuration and optimization.",
      },
      {
        icon: "CalendarDays",
        title: "Booking Systems",
        description:
          "Online scheduling, appointment management, staff calendars, and automated reminders.",
      },
      {
        icon: "ShoppingCart",
        title: "E-commerce Setup",
        description:
          "Complete product catalog, shopping cart, and streamlined checkout optimization.",
      },
      {
        icon: "Zap",
        title: "Speed Optimization",
        description:
          "Image compression, lazy loading, and full performance audits for fast load times.",
      },
    ],
    process: [
      {
        step: 1,
        icon: "Users",
        title: "Discovery Session",
        description:
          "Understanding your business goals, target audience, and complete design vision.",
        color: "#8B31FF",
      },
      {
        step: 2,
        icon: "Palette",
        title: "Custom Design",
        description:
          "Creating unique layouts and visual identity that truly differentiate your brand.",
        color: "#8B31FF",
      },
      {
        step: 3,
        icon: "Code2",
        title: "Development",
        description:
          "Building with Wix Editor and Velo to create advanced, interactive experiences.",
        color: "#6D28D9",
      },
      {
        step: 4,
        icon: "Search",
        title: "Content & SEO",
        description:
          "All content populated, SEO fully configured, and analytics connected and verified.",
        color: "#8B31FF",
      },
      {
        step: 5,
        icon: "CheckCircle",
        title: "Review & Launch",
        description:
          "Multi-device QA, revision rounds, and going live with full confidence.",
        color: "#8B31FF",
      },
    ],
    stats: [
      { value: "100+", label: "Wix Sites Built" },
      { value: "48hr", label: "First Draft" },
      { value: "100%", label: "Mobile Optimized" },
      { value: "5★", label: "Average Review" },
    ],
    techStack: [
      "Wix",
      "Velo",
      "JavaScript",
      "Wix Editor",
      "Wix Studio",
      "Google Analytics",
      "Mailchimp",
      "Zapier",
    ],
    faq: [
      {
        question: "Why choose a developer for Wix instead of doing it myself?",
        answer:
          "Wix's built-in tools are great for basic sites, but a developer unlocks custom code (Velo), advanced animations, database-driven content, booking systems, and performance optimizations that self-serve tools can't achieve.",
      },
      {
        question: "Can you use Velo to add custom functionality?",
        answer:
          "Yes. Wix Velo allows us to add custom APIs, dynamic databases, user authentication, complex form logic, third-party integrations, and virtually any JavaScript-powered feature to your Wix site.",
      },
      {
        question: "Will my Wix site rank on Google?",
        answer:
          "Yes. We configure Wix's SEO settings fully — meta titles, descriptions, structured data, sitemap, canonical URLs, and page speed optimization. Wix sites can absolutely rank well with proper setup.",
      },
      {
        question: "Can you add a booking or scheduling system?",
        answer:
          "Yes. We integrate Wix Bookings for service-based businesses, configure staff availability, payment collection, automated reminders, and client management — all within the Wix ecosystem.",
      },
      {
        question: "What is the difference between Wix Editor and Wix Studio?",
        answer:
          "Wix Studio is the professional-grade version designed for agencies and advanced users. It offers CSS control, advanced responsive layout tools, and a more powerful development environment.",
      },
      {
        question: "How long does a Wix website take to build?",
        answer:
          "A simple Wix site takes 1–2 weeks. Sites with custom Velo functionality, e-commerce, or booking systems typically take 3–5 weeks. We provide a first draft within 48 hours of receiving your content.",
      },
    ],
  },
  "ai-development": {
    tagline: "Intelligent AI Solutions That Transform How You Do Business",
    features: [
      {
        icon: "Brain",
        title: "Machine Learning",
        description:
          "Custom ML models trained on your proprietary data for accurate predictions and automation.",
      },
      {
        icon: "MessageSquare",
        title: "Natural Language Processing",
        description:
          "Chatbots, document extraction, sentiment analysis, and advanced language understanding.",
      },
      {
        icon: "Eye",
        title: "Computer Vision",
        description:
          "Real-time image recognition, object detection, and intelligent video analysis.",
      },
      {
        icon: "TrendingUp",
        title: "Predictive Analytics",
        description:
          "Demand forecasting, revenue prediction, churn detection, and anomaly monitoring.",
      },
      {
        icon: "Bot",
        title: "AI Agents & Chatbots",
        description:
          "Autonomous AI agents that resolve complex queries and handle support at any scale.",
      },
      {
        icon: "Server",
        title: "MLOps Infrastructure",
        description:
          "Scalable deployment, model versioning, drift monitoring, and automated retraining.",
      },
    ],
    process: [
      {
        step: 1,
        icon: "Database",
        title: "Data Assessment",
        description:
          "Evaluating your data sources, quality, and volume while defining measurable success metrics.",
        color: "#8B31FF",
      },
      {
        step: 2,
        icon: "Network",
        title: "Model Architecture",
        description:
          "Selecting the optimal ML/DL approach: supervised, unsupervised, or LLM-based solutions.",
        color: "#8B31FF",
      },
      {
        step: 3,
        icon: "Cpu",
        title: "Training & Tuning",
        description:
          "Iterative model training, hyperparameter optimization, and performance benchmarking.",
        color: "#6D28D9",
      },
      {
        step: 4,
        icon: "Shield",
        title: "Validation & Safety",
        description:
          "Rigorous evaluation, bias detection, hallucination testing, and error rate analysis.",
        color: "#8B31FF",
      },
      {
        step: 5,
        icon: "Rocket",
        title: "Production Deployment",
        description:
          "Scalable, monitored AI deployment integrated cleanly into your existing systems.",
        color: "#8B31FF",
      },
    ],
    stats: [
      { value: "60+", label: "AI Models Deployed" },
      { value: "91%", label: "Avg. Model Accuracy" },
      { value: "45%", label: "Cost Reduction" },
      { value: "850GB+", label: "Data Processed" },
    ],
    techStack: [
      "Python",
      "TensorFlow",
      "PyTorch",
      "OpenAI API",
      "LangChain",
      "HuggingFace",
      "AWS SageMaker",
      "FastAPI",
    ],
    faq: [
      {
        question: "Do I need a large dataset to use AI?",
        answer:
          "Not always. For structured prediction tasks, a few thousand clean records can be sufficient. For LLM-based solutions using GPT-4 or Claude, you can leverage existing foundation models with very little training data.",
      },
      {
        question: "What kind of AI use cases do you build?",
        answer:
          "Common use cases include: customer support chatbots, document processing automation, product recommendation engines, demand forecasting, fraud detection, image classification, and internal knowledge search.",
      },
      {
        question: "How do you ensure AI outputs are accurate and safe?",
        answer:
          "We use validation datasets, human-in-the-loop review processes, confidence thresholds, output filtering, and continuous monitoring in production to catch errors and model drift over time.",
      },
      {
        question: "Can you build on top of existing LLMs like GPT-4 or Claude?",
        answer:
          "Yes. We build RAG (Retrieval Augmented Generation) systems, fine-tuned models, custom AI agents, and LLM-powered automation using OpenAI, Anthropic, Google Gemini, and open-source models.",
      },
      {
        question: "How is AI integrated into existing systems?",
        answer:
          "We expose AI capabilities via REST or GraphQL APIs that integrate cleanly into your existing frontend, backend, or business tools — no rip-and-replace required.",
      },
      {
        question: "What does AI development typically cost?",
        answer:
          "Simple AI features (chatbots, basic classification) start from $10,000. Complex ML systems with custom model training, data pipelines, and MLOps infrastructure typically range from $50,000–$200,000+.",
      },
    ],
  },
  "graphic-designing": {
    tagline: "Visual Identities That Make Your Brand Truly Unforgettable",
    features: [
      {
        icon: "Layers",
        title: "Brand Identity",
        description:
          "Complete visual identity systems — logos, color palettes, typography, and usage guidelines.",
      },
      {
        icon: "PenTool",
        title: "Logo Design",
        description:
          "Distinctive logos that capture your brand's essence and stand the test of time.",
      },
      {
        icon: "Printer",
        title: "Print Collateral",
        description:
          "Business cards, brochures, packaging, and all offline marketing materials.",
      },
      {
        icon: "Monitor",
        title: "Digital Assets",
        description:
          "Social media graphics, email templates, ad banners, and website illustration sets.",
      },
      {
        icon: "Layout",
        title: "UI/UX Design",
        description:
          "Product interfaces, dashboards, and end-to-end user experience design systems.",
      },
      {
        icon: "Sparkles",
        title: "Illustration & Motion",
        description:
          "Custom illustrations, icon sets, and animated graphics for digital platforms.",
      },
    ],
    process: [
      {
        step: 1,
        icon: "Users",
        title: "Discovery & Research",
        description:
          "Deep dive into your brand story, target audience, competitors, and design direction.",
        color: "#8B31FF",
      },
      {
        step: 2,
        icon: "Lightbulb",
        title: "Concept Development",
        description:
          "Multiple distinct initial concepts exploring different visual directions and styles.",
        color: "#8B31FF",
      },
      {
        step: 3,
        icon: "Palette",
        title: "Design Refinement",
        description:
          "Iterative refinement based on your feedback until the design is absolutely right.",
        color: "#6D28D9",
      },
      {
        step: 4,
        icon: "CheckCircle",
        title: "Final Artwork",
        description:
          "Delivering all required formats: SVG, PNG, PDF, AI, PSD, and source files.",
        color: "#8B31FF",
      },
      {
        step: 5,
        icon: "BookOpen",
        title: "Brand Guidelines",
        description:
          "A comprehensive brand standards document for consistent application everywhere.",
        color: "#8B31FF",
      },
    ],
    stats: [
      { value: "280+", label: "Brands Designed" },
      { value: "48hr", label: "First Draft" },
      { value: "10", label: "Revision Rounds" },
      { value: "All", label: "File Formats" },
    ],
    techStack: [
      "Adobe Illustrator",
      "Figma",
      "Photoshop",
      "After Effects",
      "InDesign",
      "Procreate",
      "Sketch",
      "Cinema 4D",
    ],
    faq: [
      {
        question: "How many logo concepts will I receive?",
        answer:
          "You receive 3–5 distinct logo concepts in the first round. Each concept explores a different visual direction — we don't show variations of the same idea. You then choose a direction to refine.",
      },
      {
        question: "How many revision rounds are included?",
        answer:
          "Our standard package includes up to 10 revision rounds, which is more than enough for any brand project. We want you to love the result — so we don't stop until you do.",
      },
      {
        question: "What files will I receive at the end?",
        answer:
          "You receive every format you could ever need: SVG, AI, EPS (vector), PNG, JPEG (raster), and PDF. We also provide dark/light versions, horizontal/vertical layouts, and icon-only variants.",
      },
      {
        question: "Do you create brand guidelines?",
        answer:
          "Yes. Every brand identity project includes a comprehensive brand guideline document covering logo usage rules, color codes (HEX, RGB, CMYK, Pantone), typography hierarchy, and do/don't examples.",
      },
      {
        question: "Can you design social media templates?",
        answer:
          "Absolutely. We design fully customizable Canva or Figma templates for all social media formats — feed posts, stories, covers, YouTube thumbnails — matched to your new brand identity.",
      },
      {
        question: "Do you create animated logos or motion graphics?",
        answer:
          "Yes. We offer animated logo stings (for video intros), motion graphics, and animated social media assets using After Effects or Lottie (lightweight web animations).",
      },
    ],
  },
  "digital-marketing": {
    tagline: "Data-Driven Marketing Strategies That Deliver Measurable ROI",
    features: [
      {
        icon: "Search",
        title: "Search Engine Optimization",
        description:
          "Page 1 rankings through technical, on-page, and authority-building SEO strategies.",
      },
      {
        icon: "DollarSign",
        title: "Pay-Per-Click Advertising",
        description:
          "High-ROI Google Ads and Meta campaigns with precision targeting and creative testing.",
      },
      {
        icon: "Share2",
        title: "Social Media Marketing",
        description:
          "Platform-native content strategies that build community and drive direct revenue.",
      },
      {
        icon: "FileText",
        title: "Content Marketing",
        description:
          "Strategic content funnels that attract organic traffic and convert your ideal customers.",
      },
      {
        icon: "Mail",
        title: "Email Marketing",
        description:
          "Automated nurture sequences and broadcast campaigns with industry-leading open rates.",
      },
      {
        icon: "BarChart2",
        title: "Analytics & Reporting",
        description:
          "Clear performance dashboards with actionable insights delivered every month.",
      },
    ],
    process: [
      {
        step: 1,
        icon: "ClipboardList",
        title: "Digital Audit",
        description:
          "Comprehensive analysis of your current digital presence, gaps, and immediate opportunities.",
        color: "#8B31FF",
      },
      {
        step: 2,
        icon: "Target",
        title: "Strategy Development",
        description:
          "Custom multi-channel strategy with channel mix, KPIs, and detailed budget allocation.",
        color: "#8B31FF",
      },
      {
        step: 3,
        icon: "Megaphone",
        title: "Campaign Execution",
        description:
          "Multi-channel launch with precision targeting, compelling creative, and proper tracking.",
        color: "#6D28D9",
      },
      {
        step: 4,
        icon: "RefreshCw",
        title: "Continuous Optimization",
        description:
          "Weekly A/B testing, bid adjustments, creative refreshes, and audience refinements.",
        color: "#8B31FF",
      },
      {
        step: 5,
        icon: "BarChart2",
        title: "Reporting & Growth",
        description:
          "Monthly performance reviews with clear insights and next-quarter planning sessions.",
        color: "#8B31FF",
      },
    ],
    stats: [
      { value: "240%", label: "Average ROI" },
      { value: "1.8M+", label: "Monthly Reach" },
      { value: "32%", label: "Lower CPL" },
      { value: "2.4x", label: "Traffic Growth" },
    ],
    techStack: [
      "Google Ads",
      "Meta Ads",
      "SEMrush",
      "Ahrefs",
      "HubSpot",
      "Mailchimp",
      "GA4",
      "Hotjar",
    ],
    faq: [
      {
        question: "How long before I see results from SEO?",
        answer:
          "Most clients see measurable organic traffic increases within 3–4 months. Significant ranking improvements typically appear at the 6-month mark. SEO is a long-term investment — the results compound over time.",
      },
      {
        question: "What is your minimum ad spend for PPC campaigns?",
        answer:
          "We recommend a minimum of $2,000/month in ad spend for Google Ads or Meta Ads to generate statistically significant data for optimization. Management fees are separate from the media budget.",
      },
      {
        question: "How do you measure campaign success?",
        answer:
          "We track KPIs aligned to your business goals: leads generated, cost per lead, revenue attributed, ROAS (return on ad spend), organic traffic growth, and keyword rankings — all in a live reporting dashboard.",
      },
      {
        question: "Do you manage social media accounts?",
        answer:
          "Yes. We offer full social media management including content strategy, copywriting, creative design, scheduling, community management, and paid social campaigns across all major platforms.",
      },
    ],
  },
};

function getServiceContent(slug: string): ServiceContent {
  return (
    SERVICE_CONTENT[slug] || {
      tagline: "Professional Solutions Tailored to Your Business",
      features: [
        {
          icon: "Zap",
          title: "Expert Execution",
          description:
            "Delivered by a team of senior specialists with proven industry experience.",
        },
        {
          icon: "Shield",
          title: "Quality Guaranteed",
          description:
            "Every deliverable undergoes a rigorous review and QA process.",
        },
        {
          icon: "TrendingUp",
          title: "Results Focused",
          description:
            "We measure everything and optimize relentlessly for your success.",
        },
        {
          icon: "Users",
          title: "Dedicated Team",
          description:
            "A dedicated team assigned to your project from kickoff through delivery.",
        },
        {
          icon: "Clock",
          title: "On-Time Delivery",
          description:
            "Milestone-driven sprints with fully transparent progress tracking.",
        },
        {
          icon: "MessageSquare",
          title: "Clear Communication",
          description:
            "Regular updates, demos, and an always-open communication channel.",
        },
      ],
      process: [
        {
          step: 1,
          icon: "Search",
          title: "Discovery",
          description: "Understanding your goals and requirements in depth.",
          color: "#8B31FF",
        },
        {
          step: 2,
          icon: "Layout",
          title: "Planning",
          description:
            "Creating a detailed project plan and delivery timeline.",
          color: "#8B31FF",
        },
        {
          step: 3,
          icon: "Code2",
          title: "Execution",
          description: "Delivering with precision, quality, and speed.",
          color: "#6D28D9",
        },
        {
          step: 4,
          icon: "TestTube2",
          title: "Review",
          description: "Thorough review and quality assurance at every step.",
          color: "#8B31FF",
        },
        {
          step: 5,
          icon: "Rocket",
          title: "Delivery",
          description:
            "Final delivery with full documentation and ongoing support.",
          color: "#8B31FF",
        },
      ],
      stats: [
        { value: "100+", label: "Projects Delivered" },
        { value: "98%", label: "Client Satisfaction" },
        { value: "4.9/5", label: "Average Rating" },
        { value: "5+", label: "Years Experience" },
      ],
      techStack: [
        "Industry-Leading Tools",
        "Modern Frameworks",
        "Cloud Infrastructure",
        "Best Practices",
      ],
      faq: [
        {
          question: "How do we get started?",
          answer:
            "Book a free discovery call. We'll discuss your goals, scope, timeline, and budget — and provide a detailed proposal within 48 hours.",
        },
        {
          question: "How long does the project take?",
          answer:
            "Timelines vary by scope. After your discovery call we'll provide a precise milestone plan with clear delivery dates.",
        },
        {
          question: "What is your payment structure?",
          answer:
            "We typically work on a 50% deposit to start, 25% at mid-project milestone, and 25% on final delivery. Custom structures are available for larger projects.",
        },
        {
          question: "Do you provide ongoing support after delivery?",
          answer:
            "Yes. All projects include 30 days of free post-delivery support. We also offer monthly retainer plans for ongoing maintenance and enhancements.",
        },
        {
          question: "Who owns the work?",
          answer:
            "You own 100% of the deliverables, source code, and intellectual property from day one. We sign IP assignment agreements for all projects.",
        },
        {
          question: "Can you work within our existing workflow?",
          answer:
            "Absolutely. We adapt to your preferred tools — Slack, Jira, Notion, Asana, or anything else — and integrate with your development and review processes seamlessly.",
        },
      ],
    }
  );
}

// ─── Gradient helper ──────────────────────────────────────────────────────────

const GRADIENTS = [
  "from-violet-500 via-purple-500 to-indigo-500",
  "from-blue-500 via-cyan-500 to-teal-500",
  "from-green-500 via-emerald-500 to-teal-500",
  "from-orange-500 via-red-500 to-pink-500",
  "from-pink-500 via-rose-500 to-red-500",
  "from-indigo-500 via-blue-500 to-cyan-500",
];
const getGradient = (i: number) => GRADIENTS[i % GRADIENTS.length];

// Icons cycled through the hero stat cards
const STAT_ICONS = [TrendingUp, Users, Award, Clock];

// Reusable PortfolioSection-style background: dual blurred orbs + grid overlay
function SectionBackground({ isDark }: { isDark: boolean }) {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        className={`absolute top-0 -left-64 w-[500px] h-[500px] rounded-full blur-[100px] transition-all duration-700 ${isDark ? "bg-[#8B31FF]/20 opacity-100" : "bg-[#8B31FF]/10 opacity-50"}`}
      />
      <div
        className={`absolute bottom-0 -right-64 w-[500px] h-[500px] rounded-full blur-[100px] transition-all duration-700 ${isDark ? "bg-[#2B00A4]/20 opacity-100" : "bg-[#2B00A4]/10 opacity-50"}`}
      />
      <div
        className={`absolute inset-0 transition-opacity duration-700 ${isDark ? "opacity-[0.03]" : "opacity-[0.05]"}`}
        style={{
          backgroundImage: isDark
            ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
            : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />
    </div>
  );
}

// ─── Section 1: Hero ──────────────────────────────────────────────────────────

function ServiceHero({
  service,
  content,
  mode,
}: {
  service: any;
  content: ServiceContent;
  mode: string;
}) {
  const isDark = mode === "dark";
  const Icon = service
    ? (LucideIcons as any)[service.icon] || LucideIcons.Zap
    : LucideIcons.Zap;

  // Split tagline into first half (dark) and second half (purple gradient)
  const tagline =
    service?.heroHeading ||
    content?.tagline ||
    "Professional Solutions Tailored to Your Business";
  const words = tagline.split(" ");
  const half = Math.ceil(words.length / 2);
  const headingDark = words.slice(0, half).join(" ");
  const headingGrad = words.slice(half).join(" ");

  const HIGHLIGHTS = service?.heroHighlights || [];

  return (
    <section
      className={`relative min-h-screen flex items-center overflow-hidden pt-36 pb-20 lg:pt-40 lg:pb-24 transition-colors duration-700 ${isDark ? "bg-[#030712]" : "bg-white"}`}
    >
      {/* Background Ambience — PortfolioSection style */}
      <SectionBackground isDark={isDark} />

      {/* Large decorative service icon (watermark) */}
      <div
        className={`absolute -right-10 top-16 opacity-[0.05] hidden lg:block pointer-events-none ${isDark ? "text-white" : "text-[#8B31FF]"}`}
      >
        <Icon style={{ width: "380px", height: "380px" }} />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl">
          {/* Badge row */}
          <div className="hero-badge flex flex-wrap items-center gap-3 mb-6">
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border backdrop-blur-sm ${isDark ? "border-[#8B31FF]/40 bg-[#8B31FF]/15 text-[#C4B5FD]" : "border-purple-200 bg-purple-50/80 text-[#8B31FF]"}`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs font-semibold uppercase tracking-wider">
                {service?.title || "Our Services"}
              </span>
            </div>
            <div
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${isDark ? "border-white/10 bg-white/5 text-gray-300" : "border-gray-200 bg-gray-50 text-gray-600"}`}
            >
              <div className="flex items-center gap-0.5 text-amber-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={11} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <span>{service?.clientRating || "4.2/5 Client Rating"}</span>
            </div>
          </div>

          {/* Headline — no cyan, pure purple gradient */}
          <h1
            className={`hero-title text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.12] tracking-tight mb-6 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            {headingDark}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">
              {headingGrad}
            </span>
          </h1>

          {/* Description */}
          <p
            className={`hero-desc text-lg leading-relaxed mb-6 max-w-xl ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            {service?.description ||
              "Professional, results-driven solutions designed to accelerate your business growth and digital transformation."}
          </p>

          {/* Quick value-prop highlights */}
          {HIGHLIGHTS.length > 0 && (
            <div className="hero-highlights flex flex-wrap gap-x-6 gap-y-2.5 mb-8">
              {HIGHLIGHTS.map((h: string, i: number) => (
                <div
                  key={i}
                  className={`flex items-center gap-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  <CheckCircle className="w-4 h-4 text-[#8B31FF] flex-shrink-0" />
                  {h}
                </div>
              ))}
            </div>
          )}

          {/* CTAs */}
          <div className="hero-cta flex flex-wrap gap-3 sm:gap-4">
            <Link href="/contact">
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className="group cursor-pointer relative inline-flex items-center gap-2 px-6 sm:px-7 py-3.5 rounded-lg font-semibold text-sm sm:text-base bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-all duration-300 overflow-hidden"
              >
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                <span className="relative">Start a Project</span>
                <ArrowRight
                  size={17}
                  className="relative group-hover:translate-x-1 transition-transform"
                />
              </motion.button>
            </Link>
            <Link href="#projects">
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className={`inline-flex cursor-pointer items-center gap-2 px-6 sm:px-7 py-3.5 rounded-lg font-semibold text-sm sm:text-base border-2 transition-all duration-300 ${isDark ? "border-white/20 text-white hover:border-white/40 hover:bg-white/5" : "border-gray-300 text-gray-700 hover:border-[#8B31FF] hover:text-[#8B31FF]"}`}
              >
                View Our Work
                <ExternalLink size={15} />
              </motion.button>
            </Link>
          </div>

          {/* Tech stack pills */}
          <div className="hero-tech mt-8 flex flex-wrap gap-2">
            {/* <span className={`text-xs font-semibold uppercase tracking-widest self-center mr-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}>Stack:</span> */}
            {content.techStack.map((tech, i) => (
              <span
                key={i}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors duration-300 ${isDark ? "border-white/10 bg-white/5 text-gray-400" : "border-gray-200 bg-gray-50 text-gray-600 shadow-sm"}`}
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section 2: Features ──────────────────────────────────────────────────────

function ServiceFeatures({
  content,
  mode,
  serviceName,
  service,
}: {
  content: ServiceContent;
  mode: string;
  serviceName: string;
  service: any;
}) {
  const isDark = mode === "dark";

  return (
    <section
      className={`features-section relative overflow-hidden pb-24 transition-colors duration-700 ${isDark ? "bg-[#0B0F19]" : "bg-slate-50"}`}
    >
      {/* Background Ambience — PortfolioSection style */}
      <SectionBackground isDark={isDark} />

      {/* Subtle top border glow */}
      <div
        className={`relative h-px w-full ${isDark ? "bg-gradient-to-r from-transparent via-[#8B31FF]/60 to-transparent" : "bg-gradient-to-r from-transparent via-purple-200 to-transparent"}`}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4 ${isDark ? "border-[#8B31FF]/30 bg-[#8B31FF]/10 text-[#A78BFA]" : "border-purple-200 bg-purple-50 text-[#8B31FF]"}`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span className="text-[11px] font-bold uppercase tracking-wider">
              {service?.featuresBadge || "What We Offer"}
            </span>
          </div>
          <h2
            className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-tight ${isDark ? "text-white" : "text-gray-900"}`}
          >
            {service?.featuresTitle ? (
              service.featuresTitle
            ) : (
              <>
                Our{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">
                  {serviceName}
                </span>{" "}
                Expertise
              </>
            )}
          </h2>
          <p
            className={`text-base sm:text-lg max-w-2xl mx-auto leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            {service?.featuresDescription ||
              "Everything you need to succeed, delivered by a team that cares about your results as much as you do."}
          </p>
        </motion.div>

        {/* Feature cards grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {(service?.features?.length > 0
            ? service.features
            : content.features
          ).map((feature: any, i: number) => {
            const FIcon = (LucideIcons as any)[feature.icon] || LucideIcons.Zap;
            return (
              <motion.div
                key={i}
                className="feature-card group"
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{
                  duration: 0.5,
                  delay: i * 0.07,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ y: -6, transition: { duration: 0.25 } }}
              >
                <div
                  className={`relative h-full rounded-lg border p-6 overflow-hidden transition-all duration-300 ${isDark ? "bg-[#0D1117] border-white/8 hover:border-[#8B31FF]/50 hover:shadow-[0_0_30px_rgba(79,17,194,0.12)]" : "bg-white border-gray-100 hover:border-purple-200 hover:shadow-xl"}`}
                >
                  {/* Glow bg on hover */}
                  <div
                    className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 ${isDark ? "bg-gradient-to-br from-[#8B31FF]/8 via-transparent to-transparent" : "bg-gradient-to-br from-purple-50/60 via-transparent to-transparent"}`}
                  />

                  {/* Decorative large icon */}
                  <div className="absolute -right-6 -bottom-6 opacity-[0.035] group-hover:opacity-[0.06] transition-opacity duration-500">
                    <FIcon
                      style={{ width: "100px", height: "100px" }}
                      className={isDark ? "text-white" : "text-[#8B31FF]"}
                    />
                  </div>

                  <div className="relative">
                    {/* Icon circle */}
                    <div
                      className={`w-12 h-12 rounded-lg flex items-center justify-center mb-5 transition-all duration-300 ${isDark ? "bg-[#8B31FF]/20 group-hover:bg-[#8B31FF]/30" : "bg-purple-50 group-hover:bg-purple-100"}`}
                    >
                      <FIcon
                        className={`w-6 h-6 transition-colors duration-300 ${isDark ? "text-[#A78BFA] group-hover:text-white" : "text-[#8B31FF]"}`}
                      />
                    </div>

                    <h3
                      className={`font-bold text-lg mb-2 transition-colors duration-300 ${isDark ? "text-white group-hover:text-[#C4B5FD]" : "text-gray-900 group-hover:text-[#8B31FF]"}`}
                    >
                      {feature.title}
                    </h3>
                    <p
                      className={`text-sm leading-relaxed ${isDark ? "text-gray-500" : "text-gray-600"}`}
                    >
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Section 3: Process ───────────────────────────────────────────────────────

function ServiceProcess({
  content,
  mode,
  service,
}: {
  content: ServiceContent;
  mode: string;
  service: any;
}) {
  const isDark = mode === "dark";
  const processRef = useRef<HTMLDivElement>(null);
  const processTitleWords = (
    service?.processTitle ||
    content.processTitle ||
    "How We Work"
  ).split(" ");
  const processTitleStart = processTitleWords.slice(0, -1).join(" ");
  const processTitleEnd = processTitleWords.at(-1) || "";

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const ctx = gsap.context(() => {
      gsap.from(".process-step-card", {
        opacity: 0,
        x: -56,
        stagger: 0.14,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: { trigger: ".process-section-inner", start: "top 75%" },
      });
      gsap.from(".process-line-fill", {
        scaleY: 0,
        transformOrigin: "top center",
        duration: 1.4,
        ease: "power2.inOut",
        scrollTrigger: { trigger: ".process-section-inner", start: "top 72%" },
      });
    }, processRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={processRef}
      className={`relative overflow-hidden py-24 transition-colors duration-700 ${isDark ? "bg-[#030712]" : "bg-white"}`}
    >
      {/* Background Ambience — PortfolioSection style */}
      <SectionBackground isDark={isDark} />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4 ${isDark ? "border-[#8B31FF]/30 bg-[#8B31FF]/10 text-[#A78BFA]" : "border-purple-200 bg-purple-50 text-[#8B31FF]"}`}
          >
            <span className="text-[11px] font-bold uppercase tracking-wider">
              {service?.processBadge || content.processBadge || "Our Process"}
            </span>
          </div>
          <h2
            className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            {processTitleStart}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">
              {processTitleEnd}
            </span>
          </h2>
          <p
            className={`text-base sm:text-lg max-w-xl mx-auto ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            {service?.processDescription || content.processDescription || ""}
          </p>
        </motion.div>

        <div className="process-section-inner relative">
          {/* Vertical line */}
          {/* <div className="absolute left-9 sm:left-[42px] top-8 bottom-8 w-0.5 hidden sm:block overflow-hidden">
            <div
              className={`absolute inset-0 ${isDark ? "bg-white/5" : "bg-gray-100"}`}
            />
            <div className="process-line-fill absolute inset-0 bg-gradient-to-b from-[#8B31FF] via-[#8B31FF] to-[#8B31FF]" />
          </div> */}

          <div className="space-y-5">
            {(service?.process?.length > 0
              ? service.process
              : content.process
            ).map((step: any, i: number) => {
              const SIcon = (LucideIcons as any)[step.icon] || LucideIcons.Zap;
              return (
                <div
                  key={i}
                  className="process-step-card flex gap-4 sm:gap-6 items-start group"
                >
                  {/* Step icon */}
                  <div className="relative flex-shrink-0">
                    <div
                      className="w-[72px] h-[72px] sm:w-[84px] sm:h-[84px] rounded-lg flex items-center justify-center text-white shadow-lg transition-all duration-300 group-hover:scale-105 group-hover:shadow-xl"
                      style={{
                        background: `linear-gradient(135deg, ${step.color}99, ${step.color}ee)`,
                      }}
                    >
                      <SIcon className="w-7 h-7 sm:w-8 sm:h-8" />
                    </div>
                    {/* Step number badge */}
                    <div
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white text-[10px] font-extrabold flex items-center justify-center shadow-md"
                      style={{ color: step.color }}
                    >
                      {step.step}
                    </div>
                  </div>

                  {/* Content card */}
                  <div
                    className={`flex-1 rounded-lg border p-5 sm:p-6 transition-all duration-300 ${isDark ? "border-white/8 bg-white/2 group-hover:border-white/16 group-hover:bg-white/5" : "border-gray-100 bg-gray-50/70 group-hover:border-purple-200 group-hover:bg-white shadow-sm group-hover:shadow-md"}`}
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span
                        className="text-xs font-bold px-2.5 py-1 rounded-full text-white"
                        style={{ background: step.color }}
                      >
                        Step {step.step}
                      </span>
                      <h3
                        className={`font-bold text-base sm:text-lg ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {step.title}
                      </h3>
                    </div>
                    <p
                      className={`text-sm leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section 4: Projects ──────────────────────────────────────────────────────

const INITIAL_VISIBLE = 15;

function ServiceProjects({
  projects,
  loading,
  service,
  mode,
}: {
  projects: Project[];
  loading: boolean;
  service: any;
  mode: string;
}) {
  const isDark = mode === "dark";
  const [showAll, setShowAll] = useState(false);

  const visible = showAll ? projects : projects.slice(0, INITIAL_VISIBLE);
  const hasMore = projects.length > INITIAL_VISIBLE;

  const getServiceTitle = (project: Project): string => {
    if (typeof project.serviceId === "object" && project.serviceId)
      return project.serviceId.title || "Uncategorized";
    return service?.title || "Uncategorized";
  };

  const getGradient = (index: number): string => {
    const gradients = [
      "from-violet-500 via-purple-500 to-indigo-500",
      "from-blue-500 via-cyan-500 to-teal-500",
      "from-green-500 via-emerald-500 to-teal-500",
      "from-orange-500 via-red-500 to-pink-500",
      "from-pink-500 via-rose-500 to-red-500",
      "from-indigo-500 via-blue-500 to-cyan-500",
    ];
    return gradients[index % gradients.length];
  };

  return (
    <section
      id="projects"
      className={`py-24 pb-48 relative overflow-hidden transition-colors duration-700 ${isDark ? "bg-[#0B0F19]" : "bg-slate-50"}`}
    >
      {/* Background Ambience — PortfolioSection style */}
      <SectionBackground isDark={isDark} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4 ${isDark ? "border-[#8B31FF]/30 bg-[#8B31FF]/10 text-[#A78BFA]" : "border-purple-200 bg-purple-50 text-[#8B31FF]"}`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span className="text-[11px] font-bold uppercase tracking-wider">
              {service?.projectsBadge || "Our Work"}
            </span>
          </div>
          <h2
            className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            {(service?.projectsTitle || "Projects We've Built")
              .split(" ")
              .slice(0, -1)
              .join(" ")}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">
              {
                (service?.projectsTitle || "Projects We've Built")
                  .split(" ")
                  .slice(-1)[0]
              }
            </span>
          </h2>
          <p
            className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            {service?.projectsDescription ||
              `Real results for real businesses. Explore our ${service?.title?.toLowerCase() || "service"} portfolio.`}
          </p>
        </motion.div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-24 gap-x-6 pt-10">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center justify-center">
                <div
                  className={`w-[20rem] lg:w-[17rem] xl:w-[24rem] h-[28rem] rounded-lg animate-pulse ${isDark ? "bg-white/5" : "bg-gray-100"}`}
                />
              </div>
            ))}
          </div>
        )}

        {!loading && projects.length > 0 && (
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-24 gap-x-6 lg:gap-x-8 pt-10"
            >
              {visible.map((item, index) => (
                <motion.div
                  key={item._id || item.id || index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{
                    duration: 0.4,
                    ease: "easeOut",
                    delay: Math.min(index % 6, 5) * 0.08,
                  }}
                  layout
                  className="w-full flex items-center justify-center relative isolate z-0 h-[24rem]"
                >
                  <PinContainer
                    title={
                      item.liveUrl ? "Live Preview" : getServiceTitle(item)
                    }
                    href={item.liveUrl || "#"}
                    mode={mode as "light" | "dark"}
                    containerClassName="w-full h-full"
                    className="w-[20rem] h-[24rem]"
                  >
                    <div className="flex flex-col p-4 tracking-tight w-[20rem] h-[24rem]">
                      <h3
                        className={`max-w-xs !pb-2 !m-0 font-bold text-base transition-colors duration-300 ${isDark ? "text-slate-100" : "text-gray-900"}`}
                      >
                        {item.title}
                      </h3>
                      <div className="text-base !m-0 !p-0 font-normal">
                        <span
                          className={`transition-colors duration-300 line-clamp-2 ${isDark ? "text-slate-500" : "text-gray-500"}`}
                        >
                          {item.description}
                        </span>
                      </div>

                      {/* Project Image or Gradient */}
                      <div className="flex flex-1 w-full rounded-lg mt-4 overflow-hidden relative group">
                        {item.images &&
                        item.images.length > 0 &&
                        getProjectImageUrl(item.images[0]) ? (
                          <>
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                              src={getProjectImageUrl(item.images[0]) || ""}
                              alt={item.title}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display =
                                  "none";
                              }}
                            />
                            <div
                              className={`absolute inset-0 w-full h-full bg-gradient-to-br ${getGradient(index)} opacity-0`}
                            >
                              <div
                                className="absolute inset-0 opacity-20"
                                style={{
                                  backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)`,
                                }}
                              />
                            </div>
                          </>
                        ) : (
                          <div
                            className={`w-full h-full bg-gradient-to-br ${getGradient(index)} opacity-80`}
                          >
                            <div
                              className="absolute inset-0 opacity-20"
                              style={{
                                backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)`,
                              }}
                            />
                          </div>
                        )}

                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <Plus className="w-12 h-12 text-white" />
                        </div>

                        <div className="absolute top-4 left-4 px-3 py-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-xs font-semibold text-white">
                          {getServiceTitle(item)}
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mt-4">
                        {item.tags?.slice(0, 4).map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className={`px-3 py-1 rounded-full text-[10px] font-medium border transition-colors duration-300 ${
                              isDark
                                ? "bg-gray-700/50 text-gray-300 border-gray-600"
                                : "bg-gray-100 text-gray-600 border-gray-200"
                            }`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </PinContainer>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        )}

        {!loading && projects.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-20 text-center"
          >
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 ${isDark ? "bg-[#2B00A4]/30" : "bg-purple-100"}`}
            >
              <Layout
                className={`w-8 h-8 ${isDark ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
              />
            </div>
            <h3
              className={`text-xl font-semibold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              No projects yet
            </h3>
            <p className={isDark ? "text-gray-400" : "text-gray-600"}>
              Projects for this service will appear here once added.
            </p>
          </motion.div>
        )}

        {hasMore && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-center mt-20"
          >
            <button
              onClick={() => setShowAll((p) => !p)}
              className={`group relative cursor-pointer inline-flex items-center gap-3 px-8 py-4 rounded-lg font-bold text-base overflow-hidden transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] text-white`}
            >
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              {showAll ? (
                <>
                  <ChevronUp size={20} className="relative z-10" />
                  <span className="relative z-10">Show Less</span>
                </>
              ) : (
                <>
                  <span className="relative z-10">View All Projects</span>
                  <ArrowRight
                    size={20}
                    className="relative z-10 group-hover:translate-x-1 transition-transform"
                  />
                </>
              )}
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
}

// ─── Section 5: Why Choose Us (NEW) ──────────────────────────────────────────

const WHY_US = [
  {
    icon: Award,
    title: "Senior-Only Team",
    description:
      "Every project is led by experienced senior specialists. No juniors, no outsourcingyou always get our best.",
    color: "#8B31FF",
  },
  {
    icon: Clock,
    title: "On-Time, Every Time",
    description:
      "98% of our projects are delivered on schedule. Milestone-based planning keeps everything transparent and on track.",
    color: "#8B31FF",
  },
  {
    icon: Shield,
    title: "Satisfaction Guaranteed",
    description:
      "We don't consider a project done until you're completely happy. Unlimited revisions are included in every package.",
    color: "#8B31FF",
  },
  {
    icon: Headphones,
    title: "24/7 Dedicated Support",
    description:
      "A named account manager and responsive support team are with you throughout the project and long after launch.",
    color: "#A855F7",
  },
];

const COMPANY_STATS = [
  { value: "5+", label: "Years in Business" },
  { value: "25+", label: "Projects Delivered" },
  { value: "97%", label: "Satisfaction Rate" },
  { value: "30+", label: "Team Members" },
];

function ServiceWhyUs({
  mode,
  serviceName,
}: {
  mode: string;
  serviceName: string;
}) {
  const isDark = mode === "dark";

  return (
    <section
      className={`relative overflow-hidden py-24 transition-colors duration-700 ${isDark ? "bg-[#030712]" : "bg-white"}`}
    >
      {/* Background Ambience — PortfolioSection style */}
      <SectionBackground isDark={isDark} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4 ${isDark ? "border-[#8B31FF]/30 bg-[#8B31FF]/10 text-[#A78BFA]" : "border-purple-200 bg-purple-50 text-[#8B31FF]"}`}
          >
            <Award className="w-3.5 h-3.5" />
            <span className="text-[11px] font-bold uppercase tracking-wider">
              Why Choose Us
            </span>
          </div>
          <h2
            className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            The Hubmicroo{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">
              Difference
            </span>
          </h2>
          <p
            className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            What sets our {serviceName.toLowerCase()} team apart from the
            competition in the details that actually matter.
          </p>
        </motion.div>

        {/* Why Us cards grid */}
        <div className="why-us-grid grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {WHY_US.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`why-us-card rounded-lg border p-6 transition-all duration-300 ${isDark ? "border-white/8 bg-white/3 hover:border-white/16" : "border-gray-100 bg-white shadow-sm hover:shadow-md"}`}
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${item.color}20` }}
                >
                  <Icon className="w-6 h-6" style={{ color: item.color }} />
                </div>
                <h3
                  className={`font-bold text-base mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {item.title}
                </h3>
                <p
                  className={`text-sm leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Company stats bar */}
        <div
          className={`company-stats-row grid grid-cols-2 sm:grid-cols-4 gap-4 mb-14 rounded-lg border p-6 sm:p-8 ${isDark ? "border-white/8 bg-white/3" : "border-gray-100 bg-gray-50/70 shadow-sm"}`}
        >
          {COMPANY_STATS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.4, delay: i * 0.08, ease: "backOut" }}
              className="company-stat text-center"
            >
              <div className="text-2xl sm:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] mb-1">
                {s.value}
              </div>
              <div
                className={`text-xs sm:text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom trust strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className={`mt-12 rounded-lg border p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6 sm:gap-8 ${isDark ? "border-white/8 bg-gradient-to-r from-[#8B31FF]/10 to-[#8B31FF]/5" : "border-purple-100 bg-gradient-to-r from-purple-50 to-indigo-50/50"}`}
        >
          <div className="flex-1 text-center sm:text-left">
            <p
              className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Ready to experience the difference?
            </p>
            <p
              className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}
            >
              Book a free 30-minute strategy call no commitment, no sales
              pitch.
            </p>
          </div>
          <Link href="/contact">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="group relative inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-bold text-sm bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] text-white shadow-lg hover:shadow-purple-500/30 transition-all duration-300 overflow-hidden whitespace-nowrap"
            >
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              <span className="relative">Book Free Call</span>
              <ArrowRight
                size={16}
                className="relative group-hover:translate-x-1 transition-transform"
              />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Section 6: FAQ (NEW) ─────────────────────────────────────────────────────

function ServiceFAQ({
  content,
  mode,
  serviceName,
  service,
}: {
  content: ServiceContent;
  mode: string;
  serviceName: string;
  service?: any;
}) {
  const isDark = mode === "dark";
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section
      className={`relative overflow-hidden py-24 transition-colors duration-700 ${isDark ? "bg-[#0B0F19]" : "bg-slate-50"}`}
    >
      {/* Background Ambience — PortfolioSection style */}
      <SectionBackground isDark={isDark} />

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4 ${isDark ? "border-[#8B31FF]/30 bg-[#8B31FF]/10 text-[#A78BFA]" : "border-purple-200 bg-purple-50 text-[#8B31FF]"}`}
          >
            <span className="text-[11px] font-bold uppercase tracking-wider">
              FAQ
            </span>
          </div>
          <h2
            className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Common{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">
              Questions
            </span>
          </h2>
          <p
            className={`text-base sm:text-lg ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            Everything you need to know about our {serviceName.toLowerCase()}{" "}
            services.
          </p>
        </motion.div>

        {/* Accordion */}
        <div className="space-y-3">
          {(service?.faq?.length > 0 ? service.faq : content.faq).map(
            (item: any, i: number) => {
              const isOpen = openIndex === i;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                >
                  <div
                    className={`rounded-lg border overflow-hidden transition-all duration-300 ${
                      isOpen
                        ? isDark
                          ? "border-[#8B31FF]/50 shadow-[0_0_20px_rgba(79,17,194,0.15)]"
                          : "border-purple-300 shadow-md"
                        : isDark
                          ? "border-white/8 hover:border-white/16"
                          : "border-gray-100 hover:border-gray-200 shadow-sm"
                    }`}
                  >
                    {/* Question button */}
                    <button
                      onClick={() => setOpenIndex(isOpen ? null : i)}
                      className={`w-full text-left cursor-pointer flex items-start justify-between gap-4 p-5 sm:p-6 transition-colors duration-200 ${isOpen ? (isDark ? "bg-[#8B31FF]/15" : "bg-purple-50/80") : isDark ? "bg-[#0D1117] hover:bg-white/3" : "bg-white hover:bg-gray-50"}`}
                    >
                      <span
                        className={`font-semibold text-sm sm:text-base leading-snug ${
                          isOpen
                            ? "bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] bg-clip-text text-transparent"
                            : isDark
                              ? "text-white"
                              : "text-gray-900"
                        }`}
                      >
                        {item.question}
                      </span>
                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.25 }}
                        className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5 ${isOpen ? "bg-[#8B31FF] text-white" : isDark ? "bg-white/8 text-gray-400" : "bg-gray-100 text-gray-500"}`}
                      >
                        <ChevronDown size={14} />
                      </motion.div>
                    </button>

                    {/* Answer */}
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          key="answer"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.28, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div
                            className={`px-5 sm:px-6 pb-5 sm:pb-6 pt-1 border-t ${isDark ? "border-white/5 bg-[#8B31FF]/8" : "border-purple-100/50 bg-purple-50/40"}`}
                          >
                            <p
                              className={`text-sm sm:text-base leading-relaxed ${isDark ? "text-gray-300" : "text-gray-700"}`}
                            >
                              {item.answer}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            },
          )}
        </div>

        {/* Still have questions CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className={`mt-10 text-center rounded-lg border p-6 sm:p-8 ${isDark ? "border-white/8 bg-white/3" : "border-gray-100 bg-white shadow-sm"}`}
        >
          <p
            className={`font-bold text-base sm:text-lg mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Still have questions?
          </p>
          <p
            className={`text-sm mb-5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            We&apos;re happy to answer anything you want to know. No commitment required.
          </p>
          <Link href="/contact">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex cursor-pointer items-center gap-2 px-6 py-3 rounded-lg font-bold text-sm bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] text-white shadow-md hover:shadow-purple-500/30 transition-all duration-300"
            >
              Ask Us Anything
              <ArrowRight size={15} />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Section 7: CTA ───────────────────────────────────────────────────────────

function ServiceCTA({
  service,
  content,
  mode,
}: {
  service: any;
  content: ServiceContent;
  mode: string;
}) {
  const isDark = mode === "dark";
  const ctaRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const ctx = gsap.context(() => {
      gsap.from(".cta-inner", {
        opacity: 0,
        scale: 0.93,
        y: 40,
        duration: 0.9,
        ease: "power3.out",
        scrollTrigger: { trigger: ".cta-section", start: "top 80%" },
      });
    }, ctaRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={ctaRef}
      className={`cta-section relative overflow-hidden py-16 sm:py-20 lg:py-24 transition-colors duration-700 ${isDark ? "bg-[#030712]" : "bg-white"}`}
    >
      {/* Background Ambience — PortfolioSection style */}
      <SectionBackground isDark={isDark} />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="cta-inner relative rounded-lg sm:rounded-3xl overflow-hidden p-6 sm:p-12 md:p-16 text-center">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#3b0764] via-[#8B31FF] to-[#6D28D9]" />
          {/* Mesh overlay */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 50%, #8B31FF 0%, transparent 50%), radial-gradient(circle at 80% 20%, #A78BFA 0%, transparent 40%)",
            }}
          />
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
            }}
          />

          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-sm mb-5 sm:mb-6">
              <Sparkles className="w-3.5 h-3.5 text-white/80 flex-shrink-0" />
              <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-white/90">
                Ready to Start?
              </span>
            </div>

            <h2 className="text-2xl xs:text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-[1.15] px-1">
              Let&apos;s Build Something{" "}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#C4B5FD] to-[#E9D5FF] mt-1">
                Extraordinary Together
              </span>
            </h2>

            <p className="text-white/70 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed px-2">
              Ready to transform your business with world-class{" "}
              {service?.title?.toLowerCase() || "digital"} solutions? Our team
              is here to turn your vision into reality on time, on budget.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-2">
              <Link href="/contact" className="w-full sm:w-auto">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.96 }}
                  className="group cursor-pointer relative w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-bold text-sm sm:text-base bg-white text-[#8B31FF] shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
                >
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-[#8B31FF]/10 to-transparent" />
                  <span className="relative">Start Your Project</span>
                  <ArrowRight
                    size={17}
                    className="relative group-hover:translate-x-1 transition-transform flex-shrink-0"
                  />
                </motion.button>
              </Link>
              <Link href="/portfolio" className="w-full sm:w-auto">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.96 }}
                  className="group cursor-pointer relative w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-bold text-sm sm:text-base border-2 border-white/30 text-white hover:border-white/60 hover:bg-white/10 transition-all duration-300"
                >
                  View Full Portfolio
                  <ExternalLink size={15} className="flex-shrink-0" />
                </motion.button>
              </Link>
            </div>

            {/* Trust stats */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-x-4 gap-y-5 sm:gap-6 lg:gap-10 mt-8 sm:mt-10">
              {(service?.stats?.length > 0 ? service.stats : content.stats)
                .slice(0, 3)
                .map((stat: any, i: number) => (
                  <div key={i} className="text-center">
                    <div className="text-lg sm:text-xl lg:text-2xl font-extrabold text-white">
                      {stat.value}
                    </div>
                    <div className="text-[11px] sm:text-xs text-white/55 mt-0.5">
                      {stat.label}
                    </div>
                  </div>
                ))}
              <div className="text-center">
                <div className="text-lg sm:text-xl lg:text-2xl font-extrabold text-white">
                  Free
                </div>
                <div className="text-[11px] sm:text-xs text-white/55 mt-0.5">
                  Discovery Call
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  slug: string;
}

export default function ServiceClientPage({ slug }: Props) {
  const dispatch = useAppDispatch();
  const { data: services, loading: servicesLoading } = useAppSelector(
    (s) => s.landing.services,
  );
  const { data: projects, loading: projectsLoading } = useAppSelector(
    (s) => s.landing.projects,
  );
  const { mode } = useAppSelector((s) => s.theme);
  const isDark = mode === "dark";

  const containerRef = useRef<HTMLDivElement>(null);

  const service = services.find(
    (s) =>
      s.slug?.toLowerCase() === slug.toLowerCase() ||
      slugify(s.title) === slug.toLowerCase(),
  );
  const serviceId = service?._id || (service as any)?.id || "";
  const content: ServiceContent = {
    tagline: service?.heroHeading || "",
    processBadge: service?.processBadge || "Our Process",
    processTitle: service?.processTitle || "How We Work",
    processDescription: service?.processDescription || "",
    features: service?.features || [],
    process: (service?.process || []).map((step, index) => ({
      step: step.step || index + 1,
      icon: step.icon || "Zap",
      title: step.title,
      description: step.description,
      color: step.color || "#8B31FF",
    })),
    stats: service?.stats || [],
    techStack: service?.techStack || [],
    faq: service?.faq || [],
  };
  const serviceName =
    service?.title ||
    slug
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  useEffect(() => {
    dispatch(fetchServices());
  }, [dispatch]);

  useEffect(() => {
    if (servicesLoading) return;
    dispatch(
      fetchProjects({ page: 1, limit: 100, serviceId: serviceId || undefined }),
    );
  }, [serviceId, servicesLoading, dispatch]);

  // Hero GSAP entrance
  useLayoutEffect(() => {
    if (typeof window === "undefined" || servicesLoading || !service) return;
    const ctx = gsap.context(() => {
      if (!document.querySelector(".hero-title")) return;
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from(".hero-badge", { opacity: 0, y: -20, duration: 0.6 })
        .from(".hero-title", { opacity: 0, y: 50, duration: 0.8 }, "-=0.3")
        .from(".hero-desc", { opacity: 0, y: 30, duration: 0.7 }, "-=0.5")
        .from(".hero-highlights", { opacity: 0, y: 20, duration: 0.6 }, "-=0.4")
        .from(".hero-cta", { opacity: 0, y: 24, duration: 0.6 }, "-=0.4")
        .from(".hero-tech", { opacity: 0, y: 16, duration: 0.5 }, "-=0.3");
    }, containerRef);
    return () => ctx.revert();
  }, [servicesLoading, service]);

  // if (servicesLoading) {
  //   return (
  //     <div
  //       className={`min-h-screen flex items-center justify-center ${isDark ? "bg-[#030712]" : "bg-white"}`}
  //     >
  //       <div className="text-center">
  //         <div
  //           className={`w-14 h-14 rounded-full border-4 border-t-[#8B31FF] animate-spin mx-auto mb-4 ${isDark ? "border-white/10" : "border-gray-200"}`}
  //         />
  //         <p
  //           className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}
  //         >
  //           Loading service details…
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  // if (!service && !servicesLoading) {
  //   return (
  //     <div
  //       className={`min-h-screen flex items-center justify-center ${isDark ? "bg-[#030712]" : "bg-white"}`}
  //     >
  //       <div className="text-center px-4">
  //         <Search className="w-16 h-16 mx-auto mb-4 text-[#8B31FF]" />
  //         <h1
  //           className={`text-2xl font-bold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
  //         >
  //           Service Not Found
  //         </h1>
  //         <p className={`mb-6 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
  //           We couldn&apos;t find the service you&apos;re looking for.
  //         </p>
  //         <Link href="/">
  //           <button className="px-6 py-3 rounded-lg bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] text-white font-semibold hover:scale-105 transition-transform">
  //             Back to Home
  //           </button>
  //         </Link>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div ref={containerRef} className="overflow-x-hidden">
      {/* 1 — Hero */}
      <ServiceHero service={service} content={content} mode={mode} />
      {/* 2 — What We Offer */}
      <ServiceFeatures
        content={content}
        mode={mode}
        serviceName={serviceName}
        service={service}
      />
      {/* 3 — How We Work */}
      <ServiceProcess content={content} mode={mode} service={service} />
      {/* 4 — Our Work / Projects */}
      <ServiceProjects
        projects={projects}
        loading={projectsLoading}
        service={service}
        mode={mode}
      />
      {/* 5 — Why Choose Us (NEW) */}
      <ServiceWhyUs mode={mode} serviceName={serviceName} />
      {/* 6 — FAQ (NEW) */}
      <ServiceFAQ
        content={content}
        mode={mode}
        serviceName={serviceName}
        service={service}
      />
      {/* 7 — CTA */}
      <ServiceCTA service={service} content={content} mode={mode} />
    </div>
  );
}
